import React, { useCallback, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { formatVideoTime } from '../../utils/video';
import { PERCENTAGE_SCALE } from '../../utils/constants';
import * as S from '../../styles';

interface TimeProps {
	playedSeconds: number;
	duration: number;
	progress: number;
	handleSliderChange: (value: number) => void;
}

interface HoverState {
	visible: boolean;
	time: string;
	left: number;
}

const INITIAL_HOVER_STATE: HoverState = {
	visible: false,
	time: '',
	left: 0
};

export function Time({ playedSeconds, duration, progress, handleSliderChange }: Readonly<TimeProps>): JSX.Element {
	const sliderRef = useRef<HTMLDivElement>(null);
	const [hoverState, setHoverState] = useState<HoverState>(INITIAL_HOVER_STATE);
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const [draggedSeconds, setDraggedSeconds] = useState<number>(0);

	const handleMouseMove = useCallback(
		(event: MouseEvent<HTMLDivElement>): void => {
			if (!sliderRef.current || duration <= 0 || isDragging) return;

			const rect = sliderRef.current.getBoundingClientRect();
			const offsetX = event.clientX - rect.left;
			const percentage = Math.max(0, Math.min(1, offsetX / rect.width));
			const seconds = percentage * duration;

			setHoverState({
				visible: true,
				time: formatVideoTime(seconds),
				left: offsetX
			});
		},
		[duration, isDragging]
	);

	const handleMouseLeave = useCallback((): void => {
		if (!isDragging) {
			setHoverState(INITIAL_HOVER_STATE);
		}
	}, [isDragging]);

	const handleOnClick = useCallback(
		(event: MouseEvent<HTMLDivElement>): void => {
			if (!sliderRef.current || duration <= 0) return;

			const rect = sliderRef.current.getBoundingClientRect();
			const offsetX = event.clientX - rect.left;
			const percentage = Math.max(0, Math.min(1, offsetX / rect.width));
			const newProgress = percentage * PERCENTAGE_SCALE;
			handleSliderChange(newProgress);
		},
		[duration, handleSliderChange]
	);

	const handleSliderChangeInternal = useCallback(
		(value: number): void => {
			const seconds = (value / PERCENTAGE_SCALE) * duration;
			setDraggedSeconds(seconds);
			setIsDragging(true);

			if (sliderRef.current) {
				const rect = sliderRef.current.getBoundingClientRect();
				const left = (value / PERCENTAGE_SCALE) * rect.width;
				setHoverState({
					visible: true,
					time: formatVideoTime(seconds),
					left
				});
			}

			handleSliderChange(value);
		},
		[duration, handleSliderChange]
	);

	const handleAfterChange = useCallback((): void => {
		setIsDragging(false);
		setHoverState(INITIAL_HOVER_STATE);
	}, []);

	const displayedTime = isDragging ? draggedSeconds : playedSeconds;

	return (
		<>
			<S.TimeDisplay>{formatVideoTime(displayedTime)}</S.TimeDisplay>
			<S.SliderContainer
				ref={sliderRef}
				onMouseMove={handleMouseMove}
				onMouseLeave={handleMouseLeave}
				onClick={handleOnClick}
			>
				<S.TimeSliderWrapper
					value={progress}
					onChange={handleSliderChangeInternal}
					onAfterChange={handleAfterChange}
					tooltipVisible={false}
				/>
				{hoverState.visible && <S.SliderHoverTooltip $left={hoverState.left}>{hoverState.time}</S.SliderHoverTooltip>}
			</S.SliderContainer>
			<S.TimeDisplay>{formatVideoTime(duration)}</S.TimeDisplay>
		</>
	);
}
